import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Background from "../components/Background";
import AyupathCard from "../components/AyupathCard";
import PrimaryButton from "../components/PrimaryButton";
import { StarRating } from "../components/PreviewFeedback";
import { submitFeedback } from "../services/teaserApi";
import { track } from "../utils/analytics";

// Linked from under the preview and from every preview email. Every
// question is optional; answers are stored with the reader's request.
const FEELINGS = [
  ["seen", "Seen and understood"],
  ["curious", "Curious to know more"],
  ["calm", "Calm"],
  ["hopeful", "Hopeful"],
  ["unsure", "Unsure"],
  ["uneasy", "Uneasy"],
  ["nothing", "Nothing much"],
];

const PARTS = [
  ["timeline", "Where I am in my timeline"],
  ["chart", "How my chart is built"],
  ["next_periods", "What my next periods bring"],
  ["past", "The years it matched in my past"],
  ["none", "None of it"],
];

export default function Survey() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId") || "";
  const [answers, setAnswers] = useState({ feelings: [], contact_ok: false });
  const [status, setStatus] = useState("idle"); // idle | sending | done | failed

  useEffect(() => {
    if (requestId) track("survey_view", { requestId });
  }, [requestId]);

  const set = (field, value) => setAnswers((current) => ({ ...current, [field]: value }));

  function toggleFeeling(value) {
    setAnswers((current) => ({
      ...current,
      feelings: current.feelings.includes(value)
        ? current.feelings.filter((f) => f !== value)
        : [...current.feelings, value],
    }));
  }

  async function submit(event) {
    event.preventDefault();
    setStatus("sending");
    try {
      await submitFeedback(requestId, { kind: "survey", ...answers });
      track("survey_submitted", { requestId });
      setStatus("done");
    } catch {
      setStatus("failed");
    }
  }

  const empty = answers.rating == null && answers.recommend == null && !answers.feelings.length
    && !answers.most_true && !(answers.confusing || "").trim() && !(answers.worth_buying || "").trim();
  const previewLink = `/success?requestId=${encodeURIComponent(requestId)}`;

  return (
    <Background>
      <div className="min-h-screen px-4 py-10 flex flex-col items-center">
        <div className="w-full max-w-md mx-auto">
          <AyupathCard>
            {!requestId ? (
              <p className="text-sm text-gray-700">
                This survey opens from your preview or your preview email. Please use the link there.
              </p>
            ) : status === "done" ? (
              <div className="space-y-4 text-center" role="status">
                <h1 className="text-2xl font-semibold text-gray-900">Thank you</h1>
                <p className="text-gray-600">
                  Every answer is read by a person, and it shapes how AyuPath grows.
                </p>
                <Link to={previewLink} className="inline-block text-sm font-medium text-orange-600 underline">
                  Back to my preview
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-7">
                <div className="space-y-2 text-center">
                  <h1 className="text-2xl font-semibold text-gray-900">How did we do?</h1>
                  <p className="text-sm text-gray-600">About one minute. Every question is optional.</p>
                </div>

                <fieldset className="space-y-2">
                  <legend className="text-sm font-semibold text-gray-900">
                    1. How accurately did your preview reflect your life right now?
                  </legend>
                  <StarRating value={answers.rating} onChange={(v) => set("rating", v)}
                    label="How accurately did your preview reflect your life right now?" />
                </fieldset>

                <fieldset className="space-y-2">
                  <legend className="text-sm font-semibold text-gray-900">
                    2. How did reading it make you feel? <span className="font-normal text-gray-500">Choose any</span>
                  </legend>
                  <div className="flex flex-wrap gap-2">
                    {FEELINGS.map(([value, label]) => (
                      <Choice key={value} pill selected={answers.feelings.includes(value)} onClick={() => toggleFeeling(value)}>
                        {label}
                      </Choice>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="space-y-2">
                  <legend className="text-sm font-semibold text-gray-900">3. Which part felt most true?</legend>
                  <div className="space-y-1.5">
                    {PARTS.map(([value, label]) => (
                      <Choice key={value} wide selected={answers.most_true === value} onClick={() => set("most_true", value)}>
                        {label}
                      </Choice>
                    ))}
                  </div>
                </fieldset>

                <TextQuestion id="confusing" label="4. Was anything confusing, or did anything feel off?"
                  value={answers.confusing} onChange={(v) => set("confusing", v)} />

                <fieldset className="space-y-2">
                  <legend className="text-sm font-semibold text-gray-900">
                    5. How likely are you to recommend AyuPath to a friend?
                  </legend>
                  <div className="grid grid-cols-11 gap-1">
                    {Array.from({ length: 11 }, (_, n) => (
                      <Choice key={n} selected={answers.recommend === n} onClick={() => set("recommend", n)}>
                        {n}
                      </Choice>
                    ))}
                  </div>
                  <div className="flex justify-between text-[11px] text-gray-500">
                    <span>Not likely</span><span>Very likely</span>
                  </div>
                </fieldset>

                <TextQuestion id="worth_buying" label="6. What would make the full report worth buying for you?"
                  value={answers.worth_buying} onChange={(v) => set("worth_buying", v)} />

                <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={answers.contact_ok}
                    onChange={(e) => set("contact_ok", e.target.checked)}
                    className="mt-0.5 h-5 w-5 shrink-0 accent-orange-600" />
                  You may email me about my answers.
                </label>

                {status === "failed" ? (
                  <p className="text-sm text-red-700" role="alert">
                    We couldn't send your answers just now. Please try again.
                  </p>
                ) : null}
                <PrimaryButton type="submit" disabled={empty || status === "sending"}>
                  {status === "sending" ? "Sending…" : "Send my feedback"}
                </PrimaryButton>
                <p className="text-center text-xs text-gray-500">
                  Stored with your preview, used only to improve AyuPath.{" "}
                  <Link to="/privacy" className="underline">Privacy</Link>
                </p>
              </form>
            )}
          </AyupathCard>
        </div>
      </div>
    </Background>
  );
}

function Choice({ selected, onClick, pill = false, wide = false, children }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`border text-xs font-medium leading-tight transition ${
        pill ? "rounded-full px-3 py-2" : wide ? "w-full rounded-xl px-3 py-2.5 text-left text-sm" : "rounded-xl px-1 py-2.5"
      } ${selected ? "border-orange-500 bg-orange-500 text-white" : "border-gray-200 bg-white text-gray-700 hover:border-orange-300"}`}
    >
      {children}
    </button>
  );
}

function TextQuestion({ id, label, value, onChange }) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-semibold text-gray-900">{label}</label>
      <textarea id={id} rows={3} maxLength={1000} value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200" />
    </div>
  );
}
